export const ACCOUNT_EXISTS_CODE = 'auth/account-exists-with-different-credential';

/** A provider credential that could not sign in because the email already has another login. */
export interface PendingLink {
    credential: any;
    email: string | null;
    providerLabel: string;
}

/** Returns a PendingLink when the error is account-exists with a usable credential, else null. */
export function toPendingLink(err: any, providerLabel: string): PendingLink | null {
    if (err?.code !== ACCOUNT_EXISTS_CODE || !err?.credential) {
        return null;
    }
    return { credential: err.credential, email: err.email ?? err.customData?.email ?? null, providerLabel };
}

export function pendingLinkMessage(p: PendingLink): string {
    return `This email already has a Newgenus password. Enter your password below to connect ${p.providerLabel} to it.`;
}

export interface PendingLinkResult {
    linked: boolean;
    message: string;
}

/** After a successful password sign-in, attach the pending provider credential to that same user. */
export async function completePendingLink(
    user: { email?: string | null; linkWithCredential: (c: any) => Promise<unknown> } | null | undefined,
    pending: PendingLink
): Promise<PendingLinkResult> {
    const fail = (message: string): PendingLinkResult => ({ linked: false, message });
    if (!user) {
        return fail(`Signed in, but ${pending.providerLabel} could not be connected.`);
    }
    if (pending.email && user.email && pending.email.toLowerCase() !== user.email.toLowerCase()) {
        return fail(`Signed in, but ${pending.providerLabel} was not connected: it uses a different email.`);
    }
    try {
        await user.linkWithCredential(pending.credential);
        return {
            linked: true,
            message: `${pending.providerLabel} is now connected. You can use it to sign in next time.`,
        };
    } catch (err: unknown) {
        console.error('[LOGIN] pending provider link failed', (err as any)?.code);
        return fail(`Signed in, but ${pending.providerLabel} could not be connected. You can retry from Settings.`);
    }
}
